import {
    forwardRef,
    InputHTMLAttributes,
    ReactNode,
    useEffect,
    useImperativeHandle,
    useRef,
} from "react";
import Tooltip from "../Tooltip";

export default forwardRef(function TextInputWithIcon(
    {
        type = "text",
        className = "",
        isFocused = false,
        disabled = false,
        containerClassName = "",
        icon,
        error,
        ...props
    }: InputHTMLAttributes<HTMLInputElement> & {
        isFocused?: boolean;
        icon?: ReactNode;
        containerClassName?: string;
        error?: string;
    },
    ref,
) {
    const localRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
        focus: () => localRef.current?.focus(),
    }));

    useEffect(() => {
        if (isFocused) {
            localRef.current?.focus();
        }
    }, [isFocused]);

    const baseClass =
        "rounded-md border-gray-300 shadow-sm focus:border-neutral4 focus:ring-neutral4 ring-neutral4";

    const disabledClass = "opacity-50 cursor-not-allowed";

    const errorClasses = " border border-red-700";

    const input = (
        <input
            {...props}
            type={type}
            disabled={disabled}
            className={
                "flex-1 " +
                baseClass +
                (disabled ? disabledClass : "") +
                (icon ? " pl-10 " : "") +
                className +
                (error ? errorClasses : "")
            }
            ref={localRef}
            aria-invalid={!!error}
            aria-describedby={error ? `${props.id}-error` : undefined}
        />
    );

    return (
        <div className={`relative inline-flex ${containerClassName}`}>
            {icon && (
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    {icon}
                </div>
            )}
            {input}
            {error && error}
        </div>
    );
});
